(ns ui.gallery.header
  "The header of the Replicant gallery above the grid: the upload area
   (click or drop files) with the progress of running uploads, the 'New
   empty story' panel, the empty-gallery card and the full-page drop
   overlay."
  (:require [storrito.util :as util]
            [ui.elements]))

(def accept
  "The `accept` attribute of the file inputs."
  (if (util/is-safari?)
    "image/*, video/mp4, video/mov"
    ".jpg, .jpeg, image/png, video/*, .mov"))

(defn file-input
  "A hidden file input that hands the picked files to the quick upload
   and resets itself, so the same file can be picked again."
  []
  [:input.hidden
   {:type "file"
    :multiple true
    :accept accept
    :on {:change [[:gallery/upload-files :event/target.files]
                  [:dom/set-input-text :dom/node ""]]}}])

(defn upload-button
  [text]
  [:span.inline-flex.items-center.p-2.shadow-sm.text-sm.font-medium.rounded-md.text-white.bg-storrito-500
   [:div [:i.fas.fa-cloud-upload-alt.fa-2x]]
   [:div.hidden.sm:block.ml-2 text]])

(defn progress-bar
  [{:keys [name progress]}]
  (let [percent (* (or progress 0) 100.0)]
    [:div.relative.w-full.bg-gray-200.rounded-full.h-3.overflow-hidden
     (if (< percent 100)
       [:div.bg-blue-500.h-3.rounded-full
        {:style {:width (str percent "%")}}]
       [:div.bg-blue-500.h-3.rounded-full.w-full
        {:style {:animation "indeterminateAnimation 1s infinite linear"
                 :transform-origin "0% 50%"}}])
     [:span.absolute.top-0.text-black.whitespace-nowrap.w-full.text-center.overflow-hidden.text-ellipsis.font-medium
      {:class "drop-shadow-[0_1px_1px_rgba(255,255,255,1.0)]"
       :style {:font-size "10px"}}
      (str name
           " • "
           (if (< percent 100)
             (str (.toFixed percent 1) "%")
             "processing"))]]))

(defn upload-item
  [[id upload]]
  [:div.px-1.w-full.flex.justify-between.items-center
   {:replicant/key id
    :class (when (:aborted upload)
             "opacity-25")}
   [:div.w-full (progress-bar upload)]
   [:div.ml-1
    [:button.rounded-full.bg-red-300.text-white.shadow-sm.hover:bg-red-600.p-1
     {:type "button"
      :title "Abort this upload"
      :class (when-not (:abort upload)
               "invisible")
      :on {:click [[:js/stop-propagation]
                   [:js/prevent-default]
                   [:js/confirm "Do you want to abort this upload?"]
                   [:gallery/abort-upload id]]}}
     [:svg {:xmlns "http://www.w3.org/2000/svg" :fill "none" :viewBox "0 0 24 24"
            :stroke-width "1.5" :stroke "currentColor" :class ["w-3" "h-3"]}
      [:path {:stroke-linecap "round" :stroke-linejoin "round" :d "M6 18L18 6M6 6l12 12"}]]]]])

(defn upload-list
  "The progress bars of the running uploads (mirrored from the upload
   manager into the gallery state by `:gallery/watch-uploads`)."
  [gallery]
  (when-let [uploads (seq (:uploads gallery))]
    [:div.p-1.w-full.flex.flex-col
     {:class "gap-0.5"}
     (map upload-item
          (sort-by (fn [[_id upload]]
                     (:added upload))
                   uploads))]))

(defn upload-panel
  [gallery]
  [:div.hover:bg-gray-50.p-2.flex.flex-col
   {:class (concat ui.elements/panel ["w-1/2"])}
   [:label.flex.cursor-pointer.w-full.justify-center
    {:on {:dragover [[:js/prevent-default]]
          :drop [[:gallery/drop-files]]}}
    (file-input)
    (upload-button "Upload image(s) or video(s)")]
   (upload-list gallery)])

(defn create-empty-story-panel
  "Button to open the story editor without the need to upload an image
   or video first."
  []
  [:a.cursor-pointer.hover:bg-gray-50.flex.no-underline
   {:class (concat ui.elements/panel ["w-1/2" "!p-0"])
    :href "/ui/editor2"}
   [:div.flex.flex-col.justify-center.items-center.w-full.gap-2.p-2
    [:span.inline-flex.items-center.p-2.shadow-sm.text-sm.font-medium.rounded-md.text-white.bg-storrito-500
     [:i.fas.fa-edit.fa-2x]
     [:div.hidden.sm:block.ml-2 "New empty story"]]]])

(defn header-section
  [gallery]
  [:div.flex.justify-center.w-full.gap-2
   (upload-panel gallery)
   (create-empty-story-panel)])

(defn empty-card
  "Shown instead of the grid while the folder has no entries."
  []
  [:div.flex.justify-center.items-center.h-full
   {:replicant/key ::empty-card}
   [:label.cursor-pointer.items-center.flex.flex-col.sm:p-20.p-10.text-center
    {:style {:background-repeat "no-repeat"
             :background-size "contain"
             :background-position "center"
             :background-image "url(/cloud.svg)"}}
    (file-input)
    [:span.font-bold.text-xl.text-gray-600
     "Your gallery is empty!"]
    [:span.font-bold.text-gray-500.mt-2
     "Drop images / videos here or click the button"]
    [:div.mt-4.inline-flex.items-center.px-4.py-2.border.border-gray-700.hover:border-gray-600.font-medium.rounded.text-gray-100.bg-gray-800.hover:bg-gray-700
     [:i.fas.fa-upload.mr-2]
     "Upload"]]])

(defn archive-empty-hint
  []
  [:div.flex.justify-center.items-center.h-full.text-gray-500
   {:replicant/key ::archive-empty}
   "No archived items."])

(defn drop-overlay
  "Covers the page while files from the OS are dragged over it."
  []
  [:div.absolute.inset-0.flex.justify-center.items-center.text-2xl.text-gray-900.bg-white.bg-opacity-70.font-bold.border-4.border-gray-500.border-dashed.rounded-lg
   {:style {:z-index 100
            :text-shadow "1px 1px 2px #fff, 1px 1px 5px #fff, 1px 1px 10px #fff"}
    :on {:dragover [[:js/prevent-default]]
         :drop [[:gallery/drop-files]]
         :dragleave [[:gallery/file-drag-leave]]}}
   "Drop it like it's hot!"])
